import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { HelpButton } from '@/components/HelpButton';
import { EmptyState } from '@/components/EmptyState';
import { HelpText, PageHint } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import {
  matchesOwner,
  OwnerBadge,
  OwnerFilter,
  useOwnership,
  type OwnerFilterValue,
} from '@/components/OwnerBadge';
import { useToast } from '@/components/Toast';
import {
  ALL_PAYMENT_METHODS,
  PAYMENT_METHOD_LABEL,
  type AccountType,
  type PaymentMethod,
  type AccountWithBalance,
  createAccount,
  deleteAccount,
  deriveAccountName,
  fmtBRL,
  listAccounts,
  listInstitutions,
  updateAccount,
} from '@/lib/finance';

const ACCOUNT_TYPES: { value: AccountType; label: string; hint: string; warn?: string }[] = [
  { value: 'checking', label: 'Conta corrente', hint: 'Banco onde você movimenta o dia a dia' },
  { value: 'savings', label: 'Poupança', hint: 'Conta poupança do banco' },
  { value: 'wallet', label: 'Carteira (dinheiro físico)', hint: 'Dinheiro em espécie no bolso' },
  { value: 'voucher', label: 'Vale (VR/VA)', hint: 'Cartão de benefícios: refeição e/ou mercado' },
  { value: 'brokerage', label: 'Corretora', hint: 'Caixa em corretora (ex.: XP, Avenue)' },
  {
    value: 'credit_card',
    label: 'Cartão de crédito',
    hint: 'Cartão de crédito',
    warn: 'Para cartão de crédito, use a página "Cartões" — lá tem limite e fatura.',
  },
  { value: 'other', label: 'Outro', hint: 'Qualquer outro lugar com saldo' },
];

// Defaults de métodos por tipo (espelha o backend)
const DEFAULT_METHODS: Record<AccountType, PaymentMethod[]> = {
  checking: ['pix', 'debit_card', 'transfer', 'boleto', 'cash'],
  savings: ['transfer', 'pix'],
  brokerage: ['transfer'],
  wallet: ['cash'],
  credit_card: ['credit_card'],
  voucher: ['voucher'],
  other: ['pix', 'cash', 'debit_card', 'transfer', 'boleto'],
};

export function AccountsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const accountsQ = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const institutionsQ = useQuery({ queryKey: ['institutions'], queryFn: listInstitutions });
  const { myId } = useOwnership();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AccountWithBalance | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilterValue>('all');

  const visibleAccounts = (accountsQ.data ?? []).filter((a) =>
    matchesOwner(ownerFilter, a.owner_user_id, a.is_shared, myId),
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Contas</h1>
          <HelpButton topic="contas" />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="tap rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          + nova
        </button>
      </header>

      <PageHint>
        <strong>Conta</strong> = onde o dinheiro está e tem <em>saldo</em> (corrente, poupança,
        carteira, VR, VA…). Não confunda com <strong>método de pagamento</strong>.
      </PageHint>

      {accountsQ.isLoading && <p className="text-slate-500">carregando…</p>}
      {accountsQ.error && (
        <p className="text-red-600">falha: {(accountsQ.error as Error).message}</p>
      )}

      {accountsQ.data?.length === 0 && (
        <EmptyState
          title="Você ainda não tem contas"
          hint="Comece pela conta corrente principal — saldo inicial é o que está nela hoje."
          icon="🏦"
          action={
            <button
              onClick={() => setOpen(true)}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Criar primeira conta
            </button>
          }
        />
      )}

      {(accountsQ.data?.length ?? 0) > 0 && (
        <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visibleAccounts.map((a) => (
          <div
            key={a.id}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="truncate font-medium">{a.name}</p>
                  <OwnerBadge ownerId={a.owner_user_id} isShared={a.is_shared} />
                </div>
                <p className="text-xs text-slate-500">
                  {ACCOUNT_TYPES.find((t) => t.value === a.type)?.label ?? a.type}
                </p>
              </div>
              <div className="flex shrink-0 gap-3">
                <button
                  onClick={() => setEditing(a)}
                  className="text-xs text-slate-400 hover:text-slate-900"
                >
                  editar
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Excluir conta "${a.name}"?`)) {
                      deleteAccount(a.id)
                        .then(() => {
                          toast.success('conta excluída');
                          qc.invalidateQueries({ queryKey: ['accounts'] });
                          qc.invalidateQueries({ queryKey: ['dashboard'] });
                        })
                        .catch((e: Error) => toast.error(e.message));
                    }
                  }}
                  className="text-xs text-slate-400 hover:text-red-600"
                >
                  excluir
                </button>
              </div>
            </div>
            <p
              className={`mt-3 text-lg font-semibold ${
                parseFloat(a.balance) < 0 ? 'text-red-600' : 'text-slate-900'
              }`}
            >
              {fmtBRL(a.balance)}
            </p>
            {a.notes && <p className="mt-2 text-xs text-slate-500">{a.notes}</p>}
          </div>
        ))}
      </div>

      <CreateAccountModal
        open={open}
        onClose={() => setOpen(false)}
        institutions={institutionsQ.data ?? []}
        onCreated={() => {
          setOpen(false);
          toast.success('conta criada');
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />

      <EditAccountModal
        account={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          toast.success('conta atualizada');
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />
    </div>
  );
}

function EditAccountModal({
  account,
  onClose,
  onSaved,
}: {
  account: AccountWithBalance | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [isShared, setIsShared] = useState(false);
  const [notes, setNotes] = useState('');
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [error, setError] = useState<string | null>(null);

  // carrega os valores atuais quando uma conta é escolhida pra edição
  useEffect(() => {
    if (!account) return;
    setName(account.name);
    setOpeningBalance(account.opening_balance);
    setIsShared(account.is_shared);
    setNotes(account.notes ?? '');
    setMethods(account.supported_methods);
    setError(null);
  }, [account]);

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateAccount(account!.id, body),
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  if (!account) return null;
  const isCard = account.type === 'credit_card';

  function toggleMethod(m: PaymentMethod) {
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      name,
      opening_balance: openingBalance,
      is_shared: isShared,
      notes: notes || null,
      supported_methods: methods,
    });
  }

  return (
    <Modal open title={`Editar "${account.name}"`} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <Field label="Nome">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </Field>
        <Field label="Saldo inicial (R$)">
          <input
            required
            type="number"
            inputMode="decimal"
            step="0.01"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
          <HelpText>
            Ajuste aqui se lançou gastos retroativos que já estavam descontados do saldo.
          </HelpText>
        </Field>
        {!isCard && (
          <Field label="Métodos aceitos">
            <div className="flex flex-wrap gap-2">
              {ALL_PAYMENT_METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMethod(m)}
                  className={`tap rounded-full border px-3 py-1.5 text-sm ${
                    methods.includes(m)
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-300 text-slate-600'
                  }`}
                >
                  {PAYMENT_METHOD_LABEL[m]}
                </button>
              ))}
            </div>
          </Field>
        )}
        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">compartilhada com household</span>
        </label>
        <Field label="Notas (opcional)">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </Field>

        {error && <p className="text-red-600">{error}</p>}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'salvando…' : 'salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CreateAccountModal({
  open,
  onClose,
  onCreated,
  institutions,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  institutions: { id: string; name: string }[];
}) {
  const [type, setType] = useState<AccountType>('checking');
  const [institutionId, setInstitutionId] = useState<string>('');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [isShared, setIsShared] = useState(false);
  const [notes, setNotes] = useState('');
  const [methods, setMethods] = useState<PaymentMethod[]>(DEFAULT_METHODS.checking);
  const [error, setError] = useState<string | null>(null);

  // nome padronizado: "<Instituição> <Tipo curto>" — ajustável depois em "editar"
  const derivedName = deriveAccountName(
    institutions.find((i) => i.id === institutionId)?.name ?? null,
    type,
  );

  function changeType(newType: AccountType) {
    setType(newType);
    setMethods(DEFAULT_METHODS[newType]); // reseta métodos pros defaults do tipo
  }

  function toggleMethod(m: PaymentMethod) {
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  }

  const mutation = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      setType('checking');
      setInstitutionId('');
      setOpeningBalance('0');
      setIsShared(false);
      setNotes('');
      setMethods(DEFAULT_METHODS.checking);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      name: derivedName,
      type,
      institution_id: institutionId || null,
      opening_balance: openingBalance,
      is_shared: isShared,
      notes: notes || null,
      supported_methods: methods,
    });
  }

  const selected = ACCOUNT_TYPES.find((t) => t.value === type);
  const isCard = type === 'credit_card';

  return (
    <Modal open={open} title="Nova conta" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <Field label="Tipo">
          <select
            value={type}
            onChange={(e) => changeType(e.target.value as AccountType)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            {ACCOUNT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <HelpText>{selected?.hint}</HelpText>
          {selected?.warn && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              ⚠ {selected.warn}
            </p>
          )}
        </Field>
        {!isCard && (
          <Field label="Métodos de pagamento aceitos">
            <div className="flex flex-wrap gap-2">
              {ALL_PAYMENT_METHODS.filter((m) => m !== 'credit_card').map((m) => {
                const active = methods.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleMethod(m)}
                    className={`tap rounded-full border px-3 py-1.5 text-sm ${
                      active
                        ? 'border-slate-900 bg-slate-900 text-white'
                        : 'border-slate-300 text-slate-600'
                    }`}
                  >
                    {PAYMENT_METHOD_LABEL[m]}
                  </button>
                );
              })}
            </div>
            <HelpText>
              Quais formas essa conta aceita. Ao lançar uma despesa, você escolhe o método e o
              app filtra as contas que aceitam.
            </HelpText>
          </Field>
        )}
        <Field label="Instituição (opcional)">
          <select
            value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— sem instituição —</option>
            {institutions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Nome da conta">
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-base font-medium">
            {derivedName}
          </p>
          <HelpText>
            Gerado a partir da instituição + tipo. Dá pra renomear depois em "editar".
          </HelpText>
        </Field>
        <Field label="Saldo inicial (R$)">
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
          <HelpText>O quanto está nessa conta hoje.</HelpText>
        </Field>
        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">compartilhada com household</span>
        </label>
        <Field label="Notas (opcional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base"
          />
        </Field>
        {error && <p className="text-red-600">{error}</p>}
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'criando…' : 'criar conta'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-slate-700">{label}</span>
      {children}
    </label>
  );
}
